<script setup lang="ts">
import type { GradeListCategory } from '#shared/interfaces/gradeList'
import {
  ANNOTATION_PATTERN,
  TRANSITION_SOURCE,
} from '#shared/constant/constant'
import type { SendNewcarSearch } from '@/interfaces/searchUsedCar'
import { postWindowBridge } from '@/utils/windowBridge'

const { commonContentsFlg, gradeList, currentModelFlg } = defineProps<{
  commonContentsFlg: boolean
  gradeList: GradeListCategory
  currentModelFlg?: boolean
}>()
const route = useRoute()
const query = route.query
const catalogId: string = commonContentsFlg ? (route.params.id as string) : ''
const DEFAULT_ALL_GRADE_STATE = true
const isAllGrade = useState<boolean>(
  'check-toggle-btn',
  () => DEFAULT_ALL_GRADE_STATE
)
const currentPageIndex = ref(0)
const beforeModelTopChangeHistoryOn = useState(
  'before_model_top_change_history_on',
  () => new Map()
)
const beforeGradeDetailChangeHistoryOn = useState(
  'before_grade_detail_change_history_on',
  () => new Map()
)
const clickTag = ref(false)
const stickyTopOffset = computed(() => (commonContentsFlg ? 45 : 0))
const stickySectionElement = ref<HTMLElement | null>(null)
const stickyHeaderElement = ref<HTMLElement | null>(null)
const stickyMode = ref<'static' | 'fixed'>('static')
const stickyHeaderHeight = ref(0)
const stickyHeaderTop = ref(stickyTopOffset.value)
let stickyFrameId = 0
let stickySlideObserver: MutationObserver | undefined
let stickyResizeObserver: ResizeObserver | undefined

const nuxtApp = useNuxtApp()
const toggleCheckbox = () => {
  isAllGrade.value = !isAllGrade.value
  if (route.name === 'model-top-id') {
    beforeModelTopChangeHistoryOn.value.set(
      currentPageIndex.value,
      isAllGrade.value
    )
  }
  if (route.name === 'model-grade-id') {
    beforeGradeDetailChangeHistoryOn.value.set(
      currentPageIndex.value,
      isAllGrade.value
    )
  }
  if (commonContentsFlg) {
    const eventName = currentModelFlg
      ? 'CatalogCurrentGradeDetailGrdListAllTap'
      : 'CatalogPastGradeDetailGradeListAllTap'
    nuxtApp.$sendEventGaTag(eventName)
  } else {
    const eventName = currentModelFlg
      ? 'CatalogCurrentMdlTopGrdListAllTap'
      : 'CatalogPastMdlTopGradeListAllTap'
    nuxtApp.$sendEventGaTag(eventName)
  }
}

let displayCarPrice: (string | number)[] = []

const createPrice = (
  underPrice: string | undefined,
  upperPrice: string | undefined
) => {
  displayCarPrice = nuxtApp.$createDisplayCarPrice(
    Number(underPrice) * 10000,
    Number(upperPrice) * 10000
  )
  return displayCarPrice.length === 2
}

const createNewCarPrice = (price: string) => {
  if (!price) {
    return 'ーー'
  }

  return Number(price).toLocaleString()
}

const sendEventGaTag = (catalogId: number) => {
  if (!clickTag.value) {
    clickTag.value = true
    if (commonContentsFlg) {
      const eventName = currentModelFlg
        ? 'CatalogCurrentGradeDetailGrdListTap'
        : 'CatalogPastGradeDetailGradeListTap'
      nuxtApp.$sendEventGaTag(eventName)
    } else {
      const eventName = currentModelFlg
        ? 'CatalogCurrentMdlTopGrdListTap'
        : 'CatalogPastMdlTopGradeListTap'
      nuxtApp.$sendEventGaTag(eventName)
    }
    if (query.transition_source === TRANSITION_SOURCE.newcar_search) {
      const sendNewcarSearch: SendNewcarSearch = {
        url: `${useRuntimeConfig().public.BASE_URL}/model-grade/${catalogId}`,
      }
      if (
        postWindowBridge(
          'transitionCatalogApp',
          JSON.stringify(sendNewcarSearch)
        )
      ) {
        clickTag.value = false
        return
      }
    }
    navigateTo({
      path: '/model-grade/' + catalogId,
    })
  }
}
let pastGradeItemsFlg: boolean = false
const specialItemsFlg: boolean = gradeList?.special_items.length !== 0
gradeList?.nomal_items.forEach((item) => {
  if (item.past_grade_items.length !== 0) {
    pastGradeItemsFlg = true
  }
})

// グレード一覧セクションの位置から、見出しを通常表示のままにするか
// 画面上部に fixed で貼り付けるかを判定する。
// fixed にする場合は、セクション下端をはみ出さない top 位置もここで計算する。
const updateStickyHeader = () => {
  if (!stickySectionElement.value || !stickyHeaderElement.value) {
    stickyMode.value = 'static'
    return
  }

  // model-grade では、タブごとに GradeList が 1 つずつ描画されている。
  // そのため、今ユーザーに見えていないタブの中にも、このコンポーネントの別インスタンスが存在する。
  // それらまで sticky 判定を通すと、非表示タブ側の見出しまで fixed 表示の候補になってしまう。
  // fixed の見出しは Teleport で body 直下に出しているので、表示中タブの見出しとぶつかりやすい。
  // そこで、自分を含む swiper-slide が active でない場合は「今見えているタブではない」とみなし、sticky にせず通常表示のまま処理を終える。
  const currentSlide = stickySectionElement.value.closest('.swiper-slide')
  if (currentSlide && !currentSlide.classList.contains('swiper-slide-active')) {
    stickyMode.value = 'static'
    return
  }

  const sectionRect = stickySectionElement.value.getBoundingClientRect()
  const headerHeight = stickyHeaderElement.value.offsetHeight

  stickyHeaderHeight.value = headerHeight

  if (sectionRect.top > stickyTopOffset.value) {
    stickyMode.value = 'static'
    stickyHeaderTop.value = stickyTopOffset.value
    return
  }

  stickyMode.value = 'fixed'
  stickyHeaderTop.value =
    sectionRect.bottom > stickyTopOffset.value + headerHeight
      ? stickyTopOffset.value
      : sectionRect.bottom - headerHeight
}

// scroll や resize が短時間に何度発火しても、そのたびに再計算はしない。
// 次の描画フレームで 1 回だけ updateStickyHeader を実行して、無駄な計算を抑える。
const requestStickyHeaderUpdate = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (stickyFrameId) {
    return
  }

  stickyFrameId = window.requestAnimationFrame(() => {
    stickyFrameId = 0
    updateStickyHeader()
  })
}

// fixed 中は見出し本体を Teleport 側に出しているため、元の位置は空になってしまう。
// そのままだと下のリストが上に詰まるので、元の位置には同じ高さの余白だけ残す。
const stickyHeaderSpacerStyle = computed(() => {
  if (stickyMode.value !== 'fixed' || !stickyHeaderHeight.value) {
    return undefined
  }

  return {
    height: `${stickyHeaderHeight.value}px`,
  }
})

// Teleport した fixed 見出しに渡す表示位置。
// セクション末尾に近づいたら見出しを下へ押し戻すため、top を毎回計算し直している。
const stickyHeaderStyle = computed(() => {
  if (stickyMode.value !== 'fixed') {
    return undefined
  }

  return {
    top: `${stickyHeaderTop.value}px`,
  }
})

onMounted(() => {
  clickTag.value = false

  // ブラウザ履歴の position をキーにして、前回その位置で使っていた「変更履歴を表示」の ON/OFF 状態を復元する。
  currentPageIndex.value = window.history.state.position
  if (route.name === 'model-top-id') {
    // model-top 用と model-grade 用で保存 Map を分けている。
    // ここでは model-top を表示しているので、grade-detail 側の保存値はいったん空に戻す。
    beforeGradeDetailChangeHistoryOn.value = new Map()

    // 今の履歴 position に保存済みの値があれば、その ON/OFF をそのまま使う。
    if (beforeModelTopChangeHistoryOn.value.has(currentPageIndex.value)) {
      isAllGrade.value = Boolean(
        beforeModelTopChangeHistoryOn.value.get(currentPageIndex.value)
      )

      // model-top では詳細画面へ遷移して戻ると、次の history position が使われることがある。
      // その位置に以前の ON 状態が残っていると、戻った直後に意図せず展開状態が復元されるため、次の position の値は false に戻しておく。
      if (beforeModelTopChangeHistoryOn.value.get(currentPageIndex.value + 1)) {
        beforeModelTopChangeHistoryOn.value.set(
          currentPageIndex.value + 1,
          false
        )
      }
    } else {
      // 保存済みの値がなければ、共通の初期値で開始する。
      isAllGrade.value = DEFAULT_ALL_GRADE_STATE
    }
  } else if (route.name === 'model-grade-id') {
    // grade-detail でも同様に、今の履歴 position に保存された値があればそれを優先する。
    if (beforeGradeDetailChangeHistoryOn.value.has(currentPageIndex.value)) {
      isAllGrade.value = Boolean(
        beforeGradeDetailChangeHistoryOn.value.get(currentPageIndex.value)
      )
    } else {
      // 保存済みの値がなければ、共通の初期値で開始する。
      isAllGrade.value = DEFAULT_ALL_GRADE_STATE
    }
  } else {
    // 想定外の route 名では保存状態を使わず、共通の初期値で初期化する。
    isAllGrade.value = DEFAULT_ALL_GRADE_STATE
  }

  // ここから下は、sticky 見出しの位置をズラさないための監視セットアップ。
  if (typeof window === 'undefined') {
    return
  }

  const currentSlide = stickySectionElement.value?.closest('.swiper-slide')

  // model-grade のタブ切り替えは class の付け替えで active slide が変わる。
  // active / inactive の切り替えを拾って、今のタブに対する sticky 判定をやり直す。
  stickySlideObserver = new MutationObserver(() => {
    requestStickyHeaderUpdate()
  })
  if (currentSlide) {
    stickySlideObserver.observe(currentSlide, {
      attributes: true,
      attributeFilter: ['class'],
    })
  }

  // 見出し自身の高さや、トグル開閉によるセクション全体の高さが変わると固定位置の計算結果も変わるため、サイズ変化が起きたら再計算する。
  if (typeof ResizeObserver !== 'undefined') {
    stickyResizeObserver = new ResizeObserver(() => {
      requestStickyHeaderUpdate()
    })
    if (stickySectionElement.value) {
      stickyResizeObserver.observe(stickySectionElement.value)
    }
    if (stickyHeaderElement.value) {
      stickyResizeObserver.observe(stickyHeaderElement.value)
    }
  }

  // スクロール位置や viewport 幅が変わった時も sticky 判定を更新する。
  window.addEventListener('scroll', requestStickyHeaderUpdate, {
    passive: true,
  })
  window.addEventListener('resize', requestStickyHeaderUpdate)
  nextTick(() => {
    requestStickyHeaderUpdate()
  })
})

watch(
  () => isAllGrade.value,
  () => {
    // トグル切り替え直後は DOM の高さがまだ更新途中なので、描画が反映された後に sticky 位置を計算し直す。
    nextTick(() => {
      requestStickyHeaderUpdate()
    })
  }
)

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', requestStickyHeaderUpdate)
    window.removeEventListener('resize', requestStickyHeaderUpdate)
    if (stickyFrameId) {
      window.cancelAnimationFrame(stickyFrameId)
    }
  }

  stickySlideObserver?.disconnect()
  stickyResizeObserver?.disconnect()
})
</script>
<template>
  <Teleport to="body">
    <div
      v-if="gradeList && stickyMode === 'fixed'"
      class="grade-list-sticky"
      :style="stickyHeaderStyle"
    >
      <div class="container">
        <div class="grade-list-index-title">
          <span class="title-name">グレード一覧</span>
          <span v-if="pastGradeItemsFlg || specialItemsFlg" class="sub-title"
            ><span class="change-btn">変更履歴を表示</span>
            <label class="switch">
              <div
                class="slide"
                :class="{ checked: isAllGrade }"
                @click="toggleCheckbox()"
              /> </label
          ></span>
        </div>
      </div>
    </div>
  </Teleport>
  <div ref="stickySectionElement" class="grade-list-section">
    <div class="title" :style="stickyHeaderSpacerStyle">
      <div v-if="gradeList" ref="stickyHeaderElement" class="container">
        <div
          id="gradeList"
          class="grade-list-index-title"
          :style="stickyMode === 'fixed' ? { visibility: 'hidden' } : undefined"
        >
          <span class="title-name">グレード一覧</span>
          <span v-if="pastGradeItemsFlg || specialItemsFlg" class="sub-title"
            ><span class="change-btn">変更履歴を表示</span>
            <label class="switch">
              <div
                class="slide"
                :class="{ checked: isAllGrade }"
                @click="toggleCheckbox()"
              /> </label
          ></span>
        </div>
      </div>
    </div>
    <div v-for="detail in gradeList?.nomal_items" :key="detail.catalog_id">
      <div class="underscore-link" @click="sendEventGaTag(detail.catalog_id)">
        <div class="contents shadow-sm">
          <div class="container content-caset">
            <div class="content-left">
              <div class="item-title">
                {{ detail.grade_name }}
              </div>
              <div v-if="commonContentsFlg" class="item-status">
                {{ detail.debut_year }}年{{ detail.debut_month }}月
              </div>
              <div v-else class="item-status">
                {{ detail.debut_year }}年{{ detail.debut_month }}月/{{
                  detail.current_model_flg ? '現行販売車' : '新車販売終了'
                }}
              </div>
              <div class="item-carprice">
                <span class="price-title">新車時価格：</span>
                <span>
                  <span class="price-area">
                    {{ createNewCarPrice(detail.price) }}
                  </span>
                  <span class="price-unit">円</span>
                </span>
              </div>
              <div class="item-carprice used-carprice">
                <span class="price-title">中古車価格：</span>
                <span
                  v-if="
                    createPrice(
                      detail.usedcar_total_price_lower_limit,
                      detail.usedcar_total_price_upper_limit
                    )
                  "
                >
                  <span class="price-area">{{
                    Number(displayCarPrice[0]).toLocaleString()
                  }}</span
                  ><span class="hyphen">〜</span
                  ><span class="price-area">{{
                    Number(displayCarPrice[1]).toLocaleString()
                  }}</span>
                  <span class="price-unit">円</span>
                </span>

                <span v-else class="price-area">
                  {{
                    displayCarPrice[0] === 'ーー'
                      ? displayCarPrice[0]
                      : Number(displayCarPrice[0]).toLocaleString()
                  }}
                  <span class="price-unit">円</span>
                </span>
              </div>
            </div>
            <div
              v-if="catalogId !== String(detail.catalog_id)"
              class="content-right"
            >
              <img
                :class="isAllGrade ? 'after-limited-model-arrow' : ''"
                src="@/assets/image/icon/arrow_right.svg"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-for="item in detail.past_grade_items" :key="item.catalog_id">
        <div
          v-show="isAllGrade"
          class="container sub-content-caset"
          @click="sendEventGaTag(item.catalog_id)"
        >
          <div class="sub-content-left">
            <div class="item-title">
              {{ item.grade_name }}
            </div>
            <div v-if="commonContentsFlg" class="sub-item-status">
              {{ item.debut_year }}年{{ item.debut_month }}月
            </div>
            <div v-else class="sub-item-status">
              {{ item.debut_year }}年{{ item.debut_month }}月/{{
                item.current_model_flg ? '現行販売車' : '新車販売終了'
              }}
            </div>
          </div>
          <div
            v-if="catalogId !== String(item.catalog_id)"
            class="content-right"
          >
            <img src="@/assets/image/icon/arrow_right.svg" />
          </div>
        </div>
      </div>
    </div>
    <div>
      <div v-if="specialItemsFlg" class="contents">
        <div class="container content-caset">
          <div class="content-left limited-model">
            <div class="item-title">限定車・特別仕様車</div>
          </div>
          <div class="content-right limited-model-arrow">
            <img
              :class="isAllGrade ? 'after-limited-model-arrow' : ''"
              src="@/assets/image/icon/arrow_right.svg"
            />
          </div>
        </div>
      </div>
      <div
        v-for="(detail, index) in gradeList?.special_items"
        :key="detail.catalog_id"
      >
        <div
          v-show="isAllGrade"
          class="container sub-content-caset"
          :class="
            index + 1 === gradeList?.special_items.length ? 'shadow-sm' : ''
          "
          @click="sendEventGaTag(detail.catalog_id)"
        >
          <div class="sub-content-left">
            <div class="item-title">
              {{ detail.grade_name }}
            </div>
            <div v-if="commonContentsFlg" class="sub-item-status">
              {{ detail.debut_year }}年{{ detail.debut_month }}月
            </div>
            <div v-else class="sub-item-status">
              {{ detail.debut_year }}年{{ detail.debut_month }}月/{{
                detail.current_model_flg ? '現行販売車' : '新車販売終了'
              }}
            </div>
          </div>
          <div
            v-if="catalogId !== String(detail.catalog_id)"
            class="content-right"
          >
            <img src="@/assets/image/icon/arrow_right.svg" />
          </div>
        </div>
      </div>
    </div>
    <div class="container annotation">
      <div>{{ ANNOTATION_PATTERN.pattern_b }}</div>
      <div v-if="!commonContentsFlg">
        {{ ANNOTATION_PATTERN.pattern_c }}
      </div>
    </div>
  </div>
</template>
<style scoped>
.grade-list-section {
  position: relative;
}

.title {
  margin-top: 48px;
  margin-bottom: 6px;
}

.grade-list-sticky {
  background-color: #f7f8fa;
  left: 0;
  position: fixed;
  right: 0;
  z-index: 40;
}

.grade-list-index-title {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
}

.title-name {
  font-size: 20px;
  font-weight: bold;
}

.sub-title {
  align-items: center;
  display: flex;
}

.change-btn {
  font-size: 11px;
  font-weight: bold;
  margin-right: 6px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}

.switch input {
  display: none;
}

.slide {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e9e9e9;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 22px;
}

.slide:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 2px;
  bottom: 2px;
  background-color: #ffffff;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 50%;
}

.slide.checked {
  background-color: #df2020;
}

.slide.checked::before {
  -webkit-transform: translateX(18px);
  -ms-transform: translateX(18px);
  transform: translateX(18px);
}

.contents {
  background-color: #ffffff;
  border-bottom: 1px solid #f1f1f1;
  min-height: 60px;
}

.content-caset {
  display: flex;
  justify-content: space-between;
  min-height: 60px;
}

.content-left {
  padding-top: 11px;
  padding-bottom: 3px;
}

.limited-model {
  display: flex;
  align-items: center;
}

.item-title {
  font-size: 12px;
  font-weight: bold;
}

.item-status {
  font-size: 12px;
  padding-top: 2px;
}

.price-title {
  font-size: 11px;
  font-weight: bold;
}

.item-carprice {
  min-height: 14px;
  align-items: center;
  max-width: 250px;
}

.used-carprice {
  margin-top: -6px;
}

.price-area {
  font-family: 'Roboto-Bold';
  font-size: 14px !important;
  overflow-wrap: break-word;
}

.hyphen {
  font-size: 11px;
  font-weight: bold;
}

.price-unit {
  font-size: 11px;
}

.content-right {
  display: flex;
  align-items: center;
}

.content-right img {
  width: 24px;
  height: 24px;
}

.sub-content-caset {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #f1f1f1;
  background-color: #f4f4f4;
  text-decoration: none;
  color: #000000;
}

.sub-content-left {
  padding-top: 8px;
  padding-left: 8px;
}
.sub-item-status {
  font-size: 12px;
  padding-top: 8px;
  padding-bottom: 14px;
}

.limited-model-arrow {
  height: 52px;
  padding-left: 5px;
  transform: rotate(90deg);
}

.limited-model-arrow .after-limited-model-arrow {
  transform: rotate(-180deg);
}

.underscore-link {
  color: #000000;
}

.annotation {
  margin-top: 10px;
  font-size: 11px;
  white-space: pre-wrap;
}
</style>
